import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import Chart from '../../../components/ui/Chart';
import AddAutomationModal from '../../../components/modals/AddAutomationModal';
import ImportAutomationModal from '../../../components/modals/ImportAutomationModal';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';
import {
  Zap, Settings, Play, Pause, Edit, Trash2, Plus,
  Search, Target, Clock,
  CheckCircle, RefreshCw, Download, Upload,
  Activity, Timer, TrendingUp,
  Package, PlayCircle, AlertCircle,
  FileSpreadsheet, ChevronDown, X
} from 'lucide-react';
import toast from 'react-hot-toast';

const AutomationRules = () => {
  const [rules, setRules] = useState([]);
  const [selectedRule, setSelectedRule] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [showAddRule, setShowAddRule] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${month}/${day}/${year}, ${hours}:${minutes}:${seconds}`;
  });

  useEffect(() => {
    // Mock data for automation rules
    const mockRules = [
      {
        id: 1,
        name: "Auto-Assign High Priority Tickets",
        description: "Automatically assigns high priority tickets to senior agents",
        type: "Ticket Assignment",
        status: "active",
        trigger: "Ticket Created",
        conditions: [
          { field: "priority", operator: "equals", value: "high" },
          { field: "category", operator: "in", value: ["technical", "billing"] }
        ],
        actions: [
          { type: "assign", target: "senior_agents", value: "auto" },
          { type: "notify", target: "agent", message: "High priority ticket assigned" }
        ],
        executionCount: 1250,
        successRate: 96.8,
        lastExecuted: "2025-01-15T14:30:00Z",
        nextExecution: "2025-01-15T15:00:00Z",
        createdAt: "2025-01-05T10:00:00Z",
        createdBy: "AI Team",
        schedule: "immediate",
        priority: 1
      },
      {
        id: 2,
        name: "Escalate Overdue Tickets",
        description: "Escalates tickets that have been open for more than 24 hours",
        type: "Escalation",
        status: "active",
        trigger: "Time Based",
        conditions: [
          { field: "status", operator: "equals", value: "open" },
          { field: "created_at", operator: "older_than", value: "24h" }
        ],
        actions: [
          { type: "escalate", target: "manager", value: "immediate" },
          { type: "update_priority", target: "ticket", value: "high" },
          { type: "notify", target: "customer", message: "Your ticket has been escalated" }
        ],
        executionCount: 890,
        successRate: 98.2,
        lastExecuted: "2025-01-15T14:25:00Z",
        nextExecution: "2025-01-15T15:00:00Z",
        createdAt: "2025-01-03T10:00:00Z",
        createdBy: "AI Team",
        schedule: "hourly",
        priority: 2
      },
      {
        id: 3,
        name: "Auto-Response for Common Issues",
        description: "Sends automated responses for frequently asked questions",
        type: "Auto-Response",
        status: "active",
        trigger: "Ticket Created",
        conditions: [
          { field: "subject", operator: "contains", value: "password reset" },
          { field: "category", operator: "equals", value: "account" }
        ],
        actions: [
          { type: "send_response", target: "customer", message: "Password reset instructions sent" },
          { type: "update_status", target: "ticket", value: "resolved" },
          { type: "add_tag", target: "ticket", value: "auto_resolved" }
        ],
        executionCount: 2100,
        successRate: 94.5,
        lastExecuted: "2025-01-15T14:20:00Z",
        nextExecution: "2025-01-15T15:00:00Z",
        createdAt: "2025-01-08T10:00:00Z",
        createdBy: "AI Team",
        schedule: "immediate",
        priority: 3
      },
      {
        id: 4,
        name: "SLA Breach Notification",
        description: "Sends notifications when SLA is about to be breached",
        type: "Notification",
        status: "active",
        trigger: "Time Based",
        conditions: [
          { field: "sla_remaining", operator: "less_than", value: "1h" },
          { field: "status", operator: "not_equals", value: "resolved" }
        ],
        actions: [
          { type: "notify", target: "agent", message: "SLA breach warning" },
          { type: "notify", target: "manager", message: "SLA breach imminent" },
          { type: "escalate", target: "senior_agent", value: "immediate" }
        ],
        executionCount: 450,
        successRate: 99.1,
        lastExecuted: "2025-01-15T14:15:00Z",
        nextExecution: "2025-01-15T15:00:00Z",
        createdAt: "2025-01-10T10:00:00Z",
        createdBy: "AI Team",
        schedule: "every_15_minutes",
        priority: 1
      },
      {
        id: 5,
        name: "Customer Satisfaction Follow-up",
        description: "Sends follow-up emails after ticket resolution",
        type: "Follow-up",
        status: "inactive",
        trigger: "Ticket Resolved",
        conditions: [
          { field: "resolution_type", operator: "equals", value: "resolved" },
          { field: "customer_tier", operator: "in", value: ["premium", "enterprise"] }
        ],
        actions: [
          { type: "send_email", target: "customer", template: "satisfaction_survey" },
          { type: "schedule_followup", target: "system", value: "7d" }
        ],
        executionCount: 0,
        successRate: 0,
        lastExecuted: null,
        nextExecution: null,
        createdAt: "2025-01-12T10:00:00Z",
        createdBy: "AI Team",
        schedule: "daily",
        priority: 4
      },
      {
        id: 6,
        name: "Duplicate Ticket Detection",
        description: "Detects and merges duplicate tickets",
        type: "Ticket Management",
        status: "active",
        trigger: "Ticket Created",
        conditions: [
          { field: "subject_similarity", operator: "greater_than", value: "0.8" },
          { field: "customer_id", operator: "equals", value: "same" },
          { field: "created_at", operator: "within", value: "1h" }
        ],
        actions: [
          { type: "merge_tickets", target: "system", value: "auto" },
          { type: "notify", target: "customer", message: "Duplicate ticket merged" },
          { type: "add_note", target: "ticket", value: "Merged with duplicate" }
        ],
        executionCount: 320,
        successRate: 92.3,
        lastExecuted: "2025-01-15T14:10:00Z",
        nextExecution: "2025-01-15T15:00:00Z",
        createdAt: "2025-01-07T10:00:00Z",
        createdBy: "AI Team",
        schedule: "immediate",
        priority: 2
      }
    ];

    setRules(mockRules);
  }, []);

  const filteredRules = rules.filter(rule => {
    const matchesSearch = rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rule.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || rule.status === statusFilter;
    const matchesType = typeFilter === 'all' || rule.type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const handleRuleAction = (ruleId, action) => {
    setRules(rules.map(rule => 
      rule.id === ruleId 
        ? { ...rule, status: action === 'activate' ? 'active' : 'inactive' }
        : rule
    ));
    toast.success(`Rule ${action === 'activate' ? 'activated' : 'deactivated'} successfully`);
  };

  const handleTestRule = (rule) => {
    setSelectedRule(rule);
    setShowTestModal(true);
  };

  const handleSaveRule = (ruleData) => {
    if (editingRule) {
      setRules(rules.map(r => r.id === editingRule.id ? { ...r, ...ruleData } : r));
      toast.success('Rule updated successfully');
      setEditingRule(null);
    } else {
      const newRule = {
        id: rules.length + 1,
        ...ruleData,
        executionCount: 0,
        successRate: 0,
        lastExecuted: null,
        nextExecution: null,
        createdAt: new Date().toISOString(),
        createdBy: 'AI Team'
      };
      setRules([...rules, newRule]);
      toast.success('Rule added successfully');
    }
    setShowAddRule(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = now.getFullYear();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      setLastUpdated(`${month}/${day}/${year}, ${hours}:${minutes}:${seconds}`);
      toast.success('Rules refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh rules');
    } finally {
      setRefreshing(false);
    }
  };

  const handleExport = async (format = 'csv') => {
    setExporting(true);
    setShowExportMenu(false);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      if (format === 'csv') {
        const csvContent = [
          'Name,Type,Status,Trigger,Schedule,Executions,Success Rate',
          ...rules.map(rule => 
            `${rule.name},${rule.type},${rule.status},${rule.trigger},${rule.schedule},${rule.executionCount},${rule.successRate}%`
          )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `automation-rules-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
      toast.success(`Data exported as ${format.toUpperCase()} successfully`);
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteRule = (ruleId) => {
    const rule = rules.find(r => r.id === ruleId);
    if (rule) {
      setRuleToDelete(rule);
      setShowDeleteConfirm(true);
    }
  };

  const confirmDeleteRule = () => {
    if (ruleToDelete) {
      setRules(rules.filter(r => r.id !== ruleToDelete.id));
      toast.success(`Rule "${ruleToDelete.name}" deleted successfully`);
      setRuleToDelete(null);
      setShowDeleteConfirm(false);
    }
  };

  const handleImport = async (importData) => {
    try {
      const { file, format, importMode } = importData;
      
      // Simulate file processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      if (format === 'csv') {
        // Parse CSV file
        const text = await file.text();
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim());
        
        const importedRules = lines.slice(1).map((line, index) => {
          const values = line.split(',').map(v => v.trim());
          const ruleData = {};
          headers.forEach((header, i) => {
            ruleData[header] = values[i] || '';
          });

          return {
            id: rules.length + index + 1,
            name: ruleData.name || `Imported Rule ${index + 1}`,
            description: ruleData.description || 'Imported automation rule',
            type: ruleData.type || 'Ticket Assignment',
            status: ruleData.status || 'inactive',
            trigger: ruleData.trigger || 'Ticket Created',
            schedule: ruleData.schedule || 'immediate',
            priority: parseInt(ruleData.priority) || 3,
            conditions: [],
            actions: [],
            executionCount: 0,
            successRate: 0,
            lastExecuted: null,
            nextExecution: null,
            createdAt: new Date().toISOString(),
            createdBy: 'Imported'
          };
        });

        if (importMode === 'create') {
          setRules([...rules, ...importedRules]);
          toast.success(`${importedRules.length} new rules imported successfully`);
        } else if (importMode === 'update') {
          setRules(rules.map(rule => {
            const imported = importedRules.find(ir => ir.name === rule.name);
            return imported ? { ...rule, ...imported } : rule;
          }));
          toast.success('Rules updated successfully');
        } else {
          const updatedRules = [...rules];
          importedRules.forEach(imported => {
            const existingIndex = updatedRules.findIndex(r => r.name === imported.name);
            if (existingIndex >= 0) {
              updatedRules[existingIndex] = { ...updatedRules[existingIndex], ...imported };
            } else {
              updatedRules.push(imported);
            }
          });
          setRules(updatedRules);
          toast.success('Rules imported/updated successfully');
        }
      } else if (format === 'json') {
        const text = await file.text();
        const jsonData = JSON.parse(text);
        const importedRules = Array.isArray(jsonData) ? jsonData : [jsonData];
        
        const processedRules = importedRules.map((ruleData, index) => ({
          id: rules.length + index + 1,
          ...ruleData,
          conditions: ruleData.conditions || [],
          actions: ruleData.actions || [],
          executionCount: ruleData.executionCount || 0,
          successRate: ruleData.successRate || 0,
          lastExecuted: null,
          nextExecution: null,
          createdAt: new Date().toISOString(),
          createdBy: 'Imported'
        }));

        if (importMode === 'create') {
          setRules([...rules, ...processedRules]);
        } else if (importMode === 'update') {
          setRules(rules.map(rule => {
            const imported = processedRules.find(ir => ir.name === rule.name);
            return imported ? { ...rule, ...imported } : rule;
          }));
        } else {
          const updatedRules = [...rules];
          processedRules.forEach(imported => {
            const existingIndex = updatedRules.findIndex(r => r.name === imported.name);
            if (existingIndex >= 0) {
              updatedRules[existingIndex] = { ...updatedRules[existingIndex], ...imported };
            } else {
              updatedRules.push(imported);
            }
          });
          setRules(updatedRules);
        }
        toast.success(`${processedRules.length} rules imported successfully`);
      }
    } catch (error) {
      throw new Error(error.message || 'Failed to import automation rules');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      case 'testing': return 'text-blue-600 bg-blue-100';
      case 'error': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-4 w-4" />;
      case 'inactive': return <Pause className="h-4 w-4" />;
      case 'testing': return <PlayCircle className="h-4 w-4" />;
      case 'error': return <AlertCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 1: return 'text-red-600 bg-red-100';
      case 2: return 'text-orange-600 bg-orange-100';
      case 3: return 'text-yellow-600 bg-yellow-100';
      case 4: return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const chartData = {
    executionTrends: [
      { name: 'Mon', value: 45 },
      { name: 'Tue', value: 52 },
      { name: 'Wed', value: 48 },
      { name: 'Thu', value: 61 },
      { name: 'Fri', value: 55 },
      { name: 'Sat', value: 38 },
      { name: 'Sun', value: 42 }
    ],
    ruleTypes: [
      { name: 'Ticket Assignment', value: 2 },
      { name: 'Escalation', value: 1 },
      { name: 'Auto-Response', value: 1 },
      { name: 'Notification', value: 1 },
      { name: 'Follow-up', value: 1 },
      { name: 'Ticket Management', value: 1 }
    ]
  };

  const avgSuccessRate = rules.length > 0 
    ? (rules.reduce((sum, r) => sum + r.successRate, 0) / rules.length).toFixed(1)
    : 0;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Breadcrumb and Last Updated */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-500">
          <span className="text-gray-700 font-medium">AI Orchestrator</span>
          <span className="mx-2">/</span>
          <span>Automation Rules</span>
        </div>
        <div className="text-sm text-gray-500">
          Last updated: {lastUpdated}
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Automation Rules</h1>
          <p className="text-gray-600 mt-1 text-sm">Create and manage automation rules for efficient ticket processing</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 border-gray-300 hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <div className="relative">
            <Button 
              variant="outline"
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exporting}
              className="flex items-center gap-2 border-gray-300 hover:bg-gray-50"
            >
              <Download className={`h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
              {exporting ? 'Exporting...' : 'Export'}
              <ChevronDown className="h-4 w-4" />
            </Button>
            
            {showExportMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowExportMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                  <div className="py-1">
                    <button
                      onClick={() => handleExport('csv')}
                      disabled={exporting}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FileSpreadsheet className="h-4 w-4 text-green-600" />
                      Export as CSV
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          <Button 
            onClick={() => {
              setEditingRule(null);
              setShowAddRule(true);
            }} 
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add Rule
          </Button>
          <Button 
            variant="outline" 
            className="flex items-center gap-2 border-gray-300 hover:bg-gray-50"
            onClick={() => setShowImportModal(true)}
          >
            <Upload className="h-4 w-4" />
            Import
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-6">
        {/* Total Rules Card */}
        <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Total Rules</p>
                <p className="text-4xl font-extrabold text-gray-900 mb-2">{rules.length}</p>
                <p className="text-xs font-medium text-gray-600">Automation rules configured</p>
              </div>
              <div className="ml-4 p-3 bg-violet-400 rounded-xl shadow-md">
                <Zap className="h-10 w-10 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Rules Card */}
        <Card className="bg-gradient-to-br from-emerald-50 to-green-100 border-2 border-green-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-green-700 mb-2 uppercase tracking-wide">Active Rules</p>
                <p className="text-4xl font-extrabold text-green-900 mb-2">
                  {rules.filter(r => r.status === 'active').length}
                </p>
                <p className="text-xs font-medium text-green-600">Currently running</p>
              </div>
              <div className="ml-4 p-3 bg-green-500 rounded-xl shadow-md">
                <CheckCircle className="h-10 w-10 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Executions Card */}
        <Card className="bg-gradient-to-br from-pink-50 to-purple-100 border-2 border-pink-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-purple-700 mb-2 uppercase tracking-wide">Total Executions</p>
                <p className="text-4xl font-extrabold text-purple-900 mb-2">
                  {rules.reduce((sum, r) => sum + r.executionCount, 0).toLocaleString()}
                </p>
                <p className="text-xs font-medium text-purple-600">All-time executions</p>
              </div>
              <div className="ml-4 p-3 bg-purple-500 rounded-xl shadow-md">
                <Activity className="h-10 w-10 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Success Rate Card */}
        <Card className="bg-gradient-to-br from-amber-50 to-orange-100 border-2 border-amber-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-orange-700 mb-2 uppercase tracking-wide">Success Rate</p>
                <p className="text-4xl font-extrabold text-orange-900 mb-2">{avgSuccessRate}%</p>
                <p className={`text-xs font-medium ${
                  parseFloat(avgSuccessRate) >= 95 
                    ? 'text-green-600' 
                    : parseFloat(avgSuccessRate) >= 90
                    ? 'text-orange-600'
                    : 'text-red-600'
                }`}>
                  {parseFloat(avgSuccessRate) >= 95 ? 'Excellent' : parseFloat(avgSuccessRate) >= 90 ? 'Good' : 'Needs attention'}
                </p>
              </div>
              <div className="ml-4 p-3 bg-orange-500 rounded-xl shadow-md">
                <Target className="h-10 w-10 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <Card className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              Execution Trends (7 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Chart
              type="line"
              data={chartData.executionTrends}
              height={300}
              colors={['#3B82F6']}
            />
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Package className="h-5 w-5 text-purple-600" />
              Rule Types Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Chart
              type="pie"
              data={chartData.ruleTypes}
              height={300}
              colors={['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899']}
            />
          </CardContent>
        </Card>
      </div>

      {/* Rules Table */}
      <Card className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Settings className="h-5 w-5 text-gray-700" />
              Automation Rules
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search rules..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 border-gray-300">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-48 border-gray-300">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Ticket Assignment">Ticket Assignment</SelectItem>
                  <SelectItem value="Escalation">Escalation</SelectItem>
                  <SelectItem value="Auto-Response">Auto-Response</SelectItem>
                  <SelectItem value="Notification">Notification</SelectItem>
                  <SelectItem value="Follow-up">Follow-up</SelectItem>
                  <SelectItem value="Ticket Management">Ticket Management</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            {filteredRules.map((rule) => (
              <div 
                key={rule.id} 
                className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-base text-gray-900">{rule.name}</h3>
                      <Badge className={`${getStatusColor(rule.status)} rounded-full px-2.5 py-0.5 text-xs font-medium`}>
                        <div className="flex items-center gap-1">
                          {getStatusIcon(rule.status)}
                          <span className="capitalize">{rule.status}</span>
                        </div>
                      </Badge>
                      <Badge variant="outline" className="rounded-full px-2.5 py-0.5 text-xs border-gray-300">
                        {rule.type}
                      </Badge>
                      <Badge className={`${getPriorityColor(rule.priority)} rounded-full px-2.5 py-0.5 text-xs font-medium`}>
                        Priority {rule.priority}
                      </Badge>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">{rule.description}</p>
                    <div className="flex items-center gap-6 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Zap className="h-3.5 w-3.5" />
                        {rule.trigger}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {rule.schedule}
                      </span>
                      <span className="flex items-center gap-1">
                        <Activity className="h-3.5 w-3.5" />
                        {rule.executionCount.toLocaleString()} executions
                      </span>
                      <span className="flex items-center gap-1">
                        <Target className="h-3.5 w-3.5" />
                        {rule.successRate}% success
                      </span>
                      {rule.lastExecuted && (
                        <span className="flex items-center gap-1">
                          <Timer className="h-3.5 w-3.5" />
                          {new Date(rule.lastExecuted).toLocaleDateString('en-GB', { 
                            day: '2-digit', 
                            month: '2-digit' 
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleTestRule(rule)}
                      className="border-gray-300 hover:bg-gray-50"
                      title="Test Rule"
                    >
                      <PlayCircle className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRuleAction(rule.id, rule.status === 'active' ? 'deactivate' : 'activate')}
                      className="border-gray-300 hover:bg-gray-50"
                      title={rule.status === 'active' ? 'Deactivate' : 'Activate'}
                    >
                      {rule.status === 'active' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setEditingRule(rule);
                        setShowAddRule(true);
                      }}
                      className="border-gray-300 hover:bg-gray-50"
                      title="Edit Rule"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleDeleteRule(rule.id)}
                      className="border-gray-300 hover:bg-red-50 hover:border-red-300"
                      title="Delete Rule"
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Add Rule Modal */}
      <AddAutomationModal
        isOpen={showAddRule}
        onClose={() => {
          setShowAddRule(false);
          setEditingRule(null);
        }}
        automation={editingRule}
        onSave={handleSaveRule}
      />

      {/* Import Rule Modal */}
      <ImportAutomationModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImport}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setRuleToDelete(null);
        }}
        onConfirm={confirmDeleteRule}
        title="Delete Automation Rule"
        message={
          ruleToDelete 
            ? `Are you sure you want to delete "${ruleToDelete.name}"? This action cannot be undone and all execution history will be permanently removed.`
            : 'Are you sure you want to delete this rule?'
        }
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Test Rule Modal */}
      {showTestModal && selectedRule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Test Rule</h2>
                <p className="text-gray-600 text-sm mt-1">{selectedRule.name}</p>
              </div>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowTestModal(false);
                  setSelectedRule(null);
                }}
                className="border-gray-300 hover:bg-gray-50"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="space-y-5">
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Rule Conditions</h3>
                <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                  {selectedRule.conditions.map((condition, index) => (
                    <div key={index} className="text-sm mb-2 last:mb-0 flex items-center gap-2">
                      <Badge variant="outline" className="text-xs border-gray-300">
                        {condition.field}
                      </Badge>
                      <span className="text-gray-600">{condition.operator}</span>
                      <Badge variant="outline" className="text-xs border-gray-300">
                        {condition.value}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Rule Actions</h3>
                <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                  {selectedRule.actions.map((action, index) => (
                    <div key={index} className="text-sm mb-2 last:mb-0 flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-700 text-xs border-blue-200">
                        {action.type}
                      </Badge>
                      <span className="text-gray-400">→</span>
                      <span className="text-gray-600">{action.target}:</span>
                      <span className="font-medium text-gray-900">{action.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <Button 
                  onClick={() => {
                    toast.success('Test executed successfully');
                    setShowTestModal(false);
                    setSelectedRule(null);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <PlayCircle className="h-4 w-4 mr-2" />
                  Run Test
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowTestModal(false);
                    setSelectedRule(null);
                  }}
                  className="border-gray-300 hover:bg-gray-50"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutomationRules;
